import { AlertTriangle, DollarSign, Package, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

import { MetricCard } from '../../components/dashboard/MetricCard';
import { ROUTES } from '../../constants/routes';
import {
  useInventoryKpis,
  useInventoryRealtime,
  useLowStockAlerts,
  useRevenuePerProduct,
  useTopSellers,
} from '../../hooks/inventory/useInventoryDashboard';
import { InventorySettingsCard } from './components/InventorySettingsCard';
import { LowStockList } from './components/LowStockList';
import { RevenuePerProductTable } from './components/RevenuePerProductTable';
import { SalesHistoryTable } from './components/SalesHistoryTable';
import { TopSellersChart } from './components/TopSellersChart';

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export default function InventoryDashboard() {
  useInventoryRealtime();

  const kpis = useInventoryKpis();
  const topSellers = useTopSellers('30 days', 10);
  const revenuePerProduct = useRevenuePerProduct('30 days', 10);
  const lowStock = useLowStockAlerts();

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#EBFFEE]">Inventory &amp; Sales</h1>
          <p className="text-sm text-white/60 mt-1">
            Last 30 days. Updates live as POS sales close.
          </p>
        </div>
        <Link
          to={ROUTES.INVENTORY_PRODUCTS}
          className="text-sm text-white/70 hover:text-white inline-flex items-center gap-1 underline-offset-4 hover:underline whitespace-nowrap mt-1"
        >
          <Package className="w-4 h-4" /> All products
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Revenue (30d)"
          value={kpis.data ? formatCents(kpis.data.revenueCents30d) : '—'}
          icon={DollarSign}
        />
        <MetricCard
          title="Units sold (30d)"
          value={kpis.data?.unitsSold30d ?? '—'}
          icon={TrendingUp}
        />
        <MetricCard
          title="Low-stock alerts"
          value={kpis.data?.lowStockCount ?? '—'}
          icon={AlertTriangle}
          subtitle={kpis.data && kpis.data.lowStockCount > 0 ? 'Action recommended' : 'All good'}
        />
        <MetricCard
          title="Top seller (30d)"
          value={kpis.data?.topSku ?? '—'}
          icon={Package}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopSellersChart data={topSellers.data ?? []} />
        <LowStockList data={lowStock.data ?? []} />
      </div>

      <RevenuePerProductTable data={revenuePerProduct.data ?? []} />

      <SalesHistoryTable limit={50} />

      <InventorySettingsCard />
    </div>
  );
}
